import { useEffect, useMemo, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

// iter323 — First-run product tour. Fires once per user, keyed to
// `training_tour_completed` on the user record. Each role sees a
// short (5-6 stop) walkthrough of the screens they'll live in.
// Skipping counts as completion so we don't nag.

function _tourForRole(role) {
  const ADMIN = [
    { target: "[data-testid=sidebar-nav]", content: "This is your sidebar — every screen lives here. Expand a group by clicking it.", disableBeacon: true, placement: "right" },
    { target: "a[href='/clients']", content: "Start by adding your clients and sites. Sites are the atomic unit of everything else.", placement: "right", missOK: true },
    { target: "a[href='/officers']", content: "Add your officers here. Each user gets a role and a site scope you control.", placement: "right", missOK: true },
    { target: "a[href='/shifts']", content: "The Roster is where ops schedules shifts. Recurring series, drag-and-drop, and the today lane all live here.", placement: "right", missOK: true },
    { target: "a[href='/payroll']", content: "Payroll pulls from clocked attendance. Set your pay-week start (Sun/Mon) in Payroll → Settings.", placement: "right", missOK: true },
    { target: "a[href='/help']", content: "You can always find this quick-start (and role-specific guides for your team) here.", placement: "right", missOK: true },
  ];
  const OPS = [
    { target: "[data-testid=sidebar-nav]", content: "Your day lives in Operations. Roster, Attendance, Tardies, and Dispatch.", disableBeacon: true, placement: "right" },
    { target: "a[href='/shifts']", content: "The Roster shows sites × days. Click any cell to add a shift; drag to reschedule.", placement: "right", missOK: true },
    { target: "a[href='/ops-dashboard']", content: "The Ops Dashboard is your live coverage snapshot — great on a wall in kiosk mode.", placement: "right", missOK: true },
    { target: "a[href='/tardies']", content: "Tardies auto-generate when a guard is late. Use the Assign-From-Tardy modal to cover quickly.", placement: "right", missOK: true },
    { target: "a[href='/attendance']", content: "Every clock-in / clock-out shows up here. Correct times and approve ad-hoc shifts.", placement: "right", missOK: true },
    { target: "a[href='/help']", content: "Full role-specific quick-start guides are always one click away.", placement: "right", missOK: true },
  ];
  const GUARD = [
    { target: "[data-testid=sidebar-nav]", content: "Welcome — this is your on-shift home. Everything you need is a tap away.", disableBeacon: true, placement: "right" },
    { target: "a[href='/guard-portal']", content: "Today's shifts, tour scans, and clock-in / out — all here.", placement: "right", missOK: true },
    { target: "a[href='/dar']", content: "File your Daily Activity Reports here at the end of every shift.", placement: "right", missOK: true },
    { target: "a[href='/availability']", content: "Mark yourself off for a day, submit vacation requests, keep your calendar honest.", placement: "right", missOK: true },
    { target: "a[href='/help']", content: "The Guard quick-start guide covers clock-in rules, DARs, and time-off. Give it 5 minutes.", placement: "right", missOK: true },
  ];
  const CLIENT = [
    { target: "[data-testid=sidebar-nav]", content: "Welcome to your security portal. Every site and report is one click away.", disableBeacon: true, placement: "right" },
    { target: "a[href='/portal']", content: "Your dashboard — coverage now, hours delivered, and recent incidents per site.", placement: "right", missOK: true },
    { target: "a[href='/help']", content: "The Client quick-start explains weekly digests, requesting coverage, and reviewing incidents.", placement: "right", missOK: true },
  ];
  if (["super_admin", "agency_admin", "admin"].includes(role)) return { role: "admin", steps: ADMIN };
  if (["ops_manager", "supervisor", "dispatcher"].includes(role)) return { role: "ops", steps: OPS };
  if (role === "guard" || role === "officer") return { role: "guard", steps: GUARD };
  if (["client", "client_admin", "regional_manager", "site_manager"].includes(role)) return { role: "client", steps: CLIENT };
  return { role: "guard", steps: GUARD };
}

export default function TrainingTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [markedComplete, setMarkedComplete] = useState(false);

  const tour = useMemo(() => (user ? _tourForRole(user.role) : null), [user]);

  useEffect(() => {
    if (!user) return;
    // Local session flag also gates so a page reload doesn't refire mid-visit.
    const session = sessionStorage.getItem("iris_tour_shown");
    if (session === "1") return;
    if (user.training_tour_completed) return;
    // Kick off after a beat so the sidebar has painted.
    const t = setTimeout(() => {
      sessionStorage.setItem("iris_tour_shown", "1");
      setRun(true);
    }, 900);
    return () => clearTimeout(t);
  }, [user]);

  const markComplete = async () => {
    if (markedComplete || !tour) return;
    setMarkedComplete(true);
    try {
      await api.post("/help/tour-complete", { completed_tour: tour.role });
    } catch { /* best-effort; sessionStorage still stops the retry loop */ }
  };

  if (!tour) return null;

  return (
    <Joyride
      run={run}
      steps={tour.steps.map((s) => ({ ...s, disableBeacon: true }))}
      continuous
      showProgress
      showSkipButton
      hideCloseButton
      styles={{
        options: {
          zIndex: 10001,
          primaryColor: "#FF3B30",
          textColor: "#f4f4f5",
          backgroundColor: "#121212",
          arrowColor: "#121212",
          overlayColor: "rgba(0,0,0,0.55)",
        },
        tooltipContainer: { textAlign: "left" },
        buttonNext: { fontFamily: "inherit", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" },
        buttonBack: { fontFamily: "inherit", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "#a1a1aa" },
        buttonSkip: { fontFamily: "inherit", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "#71717a" },
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Got it",
        next: "Next",
        skip: "Skip",
      }}
      callback={(data) => {
        const { status, action } = data;
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
          setRun(false);
          markComplete();
          // If the tour ended on the Help step, drop the user there so
          // they land on the deeper docs while it's fresh.
          if (action === "next" && status === STATUS.FINISHED) {
            navigate("/help");
          }
        }
      }}
    />
  );
}
